<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { computed, onDeactivated, ref, watch } from 'vue';
import type { Series } from '@ilteducation/content-registry-types';
import useLocale from './helpers/use-locale';
import { useKeepAliveDeactivateAwareKey } from './helpers/use-keep-alive-deactivate-aware-key';
import { useTooltipManager } from './helpers/use-tooltip-manager';

type Emits = {
  (e: 'on-card-click'): void;
  (e: 'on-card-select'): void;
  (e: 'is-hovering', isHovering: boolean): void;
};
const emit = defineEmits<Emits>();
type Props = {
  type?: 'image' | 'icon';
  hasZoomOnHover?: boolean;
  selected?: boolean;
  contentCardWidth?: string | number;
  contentCardHeight?: string | number;
  contentCardAspectRatio?: string | number;
  hasStack?: boolean;
  imageHeight: string | number;
  isRounded?: boolean;
  title?: string;
  authors?: string;
  serie?: Series;
  overflowHidden?: boolean;
  hoverRoundedClass?: string;
  scaleValue?: number;
  /**
   * Width of the caption block. Defaults to the card width, which is the *box* — pass the cover's own
   * width when the cover is narrower than its box, or the caption starts left of the image it names.
   */
  titleWidth?: string | number;
};
const props = withDefaults(defineProps<Props>(), {
  type: 'image',
  hasZoomOnHover: true,
  selected: false,
  contentCardWidth: 128,
  contentCardHeight: 180,
  contentCardAspectRatio: undefined,
  imageHeight: 180,
  isRounded: true,
  overflowHidden: true,
  hoverRoundedClass: 'rounded-lg',
  scaleValue: 1.08,
  titleWidth: undefined,
});

// Centred in the box so its left edge lands on the cover's, which MaterialPreview also centres.
const titleStyle = computed(() =>
  props.titleWidth
    ? { width: `${props.titleWidth}px`, maxWidth: `${props.contentCardWidth}px`, marginInline: 'auto' }
    : { maxWidth: `${props.contentCardWidth}px` },
);

const { platform } = useDisplay();
const { t } = useLocale();
const { activeTooltip, setActiveTooltip, resetTooltip } = useTooltipManager();

const isHovering = ref(false);

const isTouchDevice = computed(() => platform.value.touch);

const tooltipContent = computed(() => {
  const lines: string[] = [];
  if (props.title) {
    lines.push(`${t('components.title')}: ${props.title}`);
  }
  if (props.authors) {
    lines.push(`${t('components.author')}: ${props.authors}`);
  }
  if (props.serie && props.serie.name) {
    lines.push(`${t('label_serie_name')}: ${props.serie.name}`);
  }
  if (props.serie && props.serie.readingOrder) {
    lines.push(`${t('label_serie_part')}: ${props.serie.readingOrder}`);
  }
  return lines.length ? lines.join('\n') : null;
});

const { key: tooltipKey } = useKeepAliveDeactivateAwareKey();

watch(isHovering, (value) => {
  emit('is-hovering', value);
});

const tooltipId = 'content-hover-card-tooltip-' + tooltipKey.value;

const showTooltip = computed(() => {
  return isHovering.value && activeTooltip.value !== 'feature-bubble-tooltip';
});

watch([isHovering], () => {
  if (!isHovering.value) {
    resetTooltip();
    return;
  }

  if (activeTooltip.value !== 'feature-bubble-tooltip') {
    setActiveTooltip(tooltipId);
  }
});

const enterContent = () => {
  closeTooltip();
  emit('on-card-click');
};

const closeTooltip = () => {
  isHovering.value = false;
  resetTooltip();
};

onDeactivated(() => {
  closeTooltip();
});
</script>

<template>
  <v-card
    variant="flat"
    class="content-hover-card rounded-0"
    :class="{
      'd-flex': true,
      'flex-column': true,
      'align-center': false,
    }"
    :ripple="false"
    @mouseenter="isTouchDevice ? () => {} : (isHovering = true)"
    @mouseleave="isHovering = false"
    @blur="isHovering = false"
    @focus="isHovering = true"
    @touch="isHovering = false"
    @click="enterContent"
    @keydown.enter="enterContent"
    @keydown.space="emit('on-card-select')"
  >
    <v-card-item
      class="content-card-item position-relative pa-0"
      :class="{
        'selected-content': selected,
        'content-card-image': type === 'image',
        'content-card-icon': type === 'icon',
        [`elevation-${selected ? 12 : isHovering ? 8 : 4}`]: type === 'icon',
      }"
      :style="{
        width: `${contentCardWidth}px`,
        height: `${contentCardHeight}px`,
        'aspect-ratio': contentCardAspectRatio ? contentCardAspectRatio : undefined,
      }"
    >
      <div
        class="content-card-item-images"
        :class="{
          'content-hover-card-zoom': hasZoomOnHover && !isTouchDevice,
          [`${hoverRoundedClass}`]: isRounded,
          'overflow-hidden-on-hover': overflowHidden,
        }"
        :style="`height: ${imageHeight ?? contentCardHeight}`"
      >
        <slot name="image"></slot>
        <slot name="coverFeatures"></slot>
        <div class="content-card-stack">
          <slot name="stack"></slot>
        </div>
      </div>
      <v-tooltip
        v-model="showTooltip"
        :key="tooltipKey"
        v-if="tooltipContent"
        :disabled="isTouchDevice"
        location="bottom"
        activator="parent"
        :text="tooltipContent"
        class="tooltip"
        :open-on-hover="false"
      />
    </v-card-item>
    <div class="content-card-title pa-0" :style="titleStyle">
      <slot name="title"></slot>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.content-hover-card {
  height: fit-content !important;
  width: fit-content !important;
  overflow: visible;
  background: none;
}

.content-card-stack {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

:deep(.v-card__overlay) {
  background-color: transparent !important;
}

.content-card-item {
  &-images {
    position: relative;
    overflow: visible;
  }
}
.content-hover-card-zoom {
  transition: outline-color 0.3s ease-out;
  outline: solid 3px transparent !important;
  outline-offset: 2px;
}

:deep(.v-card-item__content) {
  overflow: visible !important;
}

.content-card-item .content-hover-card-zoom {
  transition: ease-in-out 0.3s all;

  &.content-card-icon {
    display: inline-block;
    background-color: transparent;
    height: fit-content;
    border-radius: 50%;
  }

  &:focus-visible {
    transform: scale(v-bind(scaleValue));
    outline: none !important;
  }

  /* Styles for devices with hover capability */
  @media (hover: hover) {
    &:hover,
    &:focus-within:not(:hover) {
      transform: scale(v-bind(scaleValue));
      transition: transform 0.3s ease-in-out, outline-color 0.3s ease-in;
      outline: solid 3px rgb(var(--v-theme-primary)) !important;

      .content-card-stack {
        display: none;
      }
    }

    &:active,
    &:focus,
    &:focus:not(:hover) {
      transform: scale(0.95);
      transition: transform 0.3s ease-in-out, outline-color 0.3s ease-in;
      outline: solid 3px rgb(var(--v-theme-primary)) !important;
    }
  }

  /* Styles for devices without hover (e.g., touchscreens) */
  @media (hover: none) {
    &:focus-within {
      transform: scale(1.02);
      transition: transform 0.2s ease-in-out, outline-color 0.2s ease-in;
      outline: solid 2px rgb(var(--v-theme-primary)) !important;
    }

    &:active {
      transform: scale(0.98);
      transition: transform 0.15s ease-in-out, outline-color 0.15s ease-in;
      outline: solid 2px rgb(var(--v-theme-primary)) !important;
    }
  }

  /* Dynamically controlled overflow */
  &.overflow-hidden-on-hover {
    @media (hover: hover) {
      &:hover,
      &:focus-within:not(:hover) {
        overflow: hidden;
      }

      &:active,
      &:focus,
      &:focus:not(:hover) {
        overflow: hidden;
      }
    }

    @media (hover: none) {
      &:focus-within {
        overflow: hidden;
      }

      &:active {
        overflow: hidden;
      }
    }
  }
}
.tooltip ::v-deep(.v-overlay__content) {
  white-space: pre-wrap;
  word-wrap: break-word;
  transform: translateY(5px);
  opacity: 0.8;
}
</style>
